import * as React from 'react';
import { Box, Typography, Button, Container, Stack, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HiPlay, HiInformationCircle } from 'react-icons/hi';

const FloatingShape = ({ delay, size, x, y, color }: { delay: number; size: number; x: number; y: number; color: string }) => (
  <motion.div
    style={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '50%',
      background: `radial-gradient(circle, ${color}40 0%, transparent 70%)`,
      left: `${x}%`,
      top: `${y}%`,
      pointerEvents: 'none',
      filter: 'blur(40px)',
    }}
    animate={{
      x: [0, 30, -20, 0],
      y: [0, -40, 20, 0],
      scale: [1, 1.2, 0.9, 1],
    }}
    transition={{
      duration: 8 + delay,
      repeat: Infinity,
      ease: 'easeInOut',
      delay,
    }}
  />
);

export default function LandingHero() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '90vh', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#050505',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.15) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(0,180,216,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <FloatingShape delay={0} size={400} x={10} y={20} color="#8B5CF6" />
      <FloatingShape delay={1} size={300} x={70} y={10} color="#00B4D8" />
      <FloatingShape delay={2} size={350} x={50} y={60} color="#EC4899" />
      <FloatingShape delay={0.5} size={250} x={85} y={70} color="#8B5CF6" />
      <FloatingShape delay={1.5} size={200} x={5} y={50} color="#00FFEF" />

      {/* Grid overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, px: { xs: 2, md: 4 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.175, 0.885, 0.32, 1.275] }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2.5,
                py: 1,
                borderRadius: 100,
                border: '1px solid rgba(139,92,246,0.3)',
                background: 'rgba(139,92,246,0.1)',
                backdropFilter: 'blur(10px)',
                mb: 4,
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981', animation: 'pulse 2s infinite' }} />
              <Typography sx={{ fontSize: '0.875rem', color: '#CBD5E1', fontWeight: 500 }}>
                Now streaming 10,000+ anime titles
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.175, 0.885, 0.32, 1.275] }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
                fontWeight: 900,
                lineHeight: 1.05,
                letterSpacing: '-0.03em',
                mb: 3,
                background: 'linear-gradient(135deg, #F8FAFC 0%, #8B5CF6 40%, #00B4D8 70%, #EC4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Discover Your Next
              <br />
              Anime Obsession
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.175, 0.885, 0.32, 1.275] }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.25rem' },
                color: '#94A3B8',
                maxWidth: 600,
                mx: 'auto',
                mb: 5,
                lineHeight: 1.7,
              }}
            >
              Stream thousands of anime series and movies in stunning HD.
              From classics to the latest seasonal hits — all in one place, free.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: [0.175, 0.885, 0.32, 1.275] }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/home')}
                startIcon={<HiPlay size={20} />}
                sx={{
                  background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 4,
                  py: 1.8,
                  borderRadius: 3,
                  textTransform: 'none',
                  minWidth: 200,
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(139,92,246,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #9C6CF7, #8B4DEA)',
                    boxShadow: '0 12px 40px rgba(139,92,246,0.5)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Start Watching
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/browse')}
                startIcon={<HiInformationCircle size={20} />}
                sx={{
                  borderColor: alpha('#fff', 0.2),
                  color: '#F8FAFC',
                  fontWeight: 600,
                  fontSize: '1rem',
                  px: 4,
                  py: 1.8,
                  borderRadius: 3,
                  textTransform: 'none',
                  minWidth: 200,
                  '&:hover': {
                    borderColor: alpha('#fff', 0.4),
                    background: alpha('#fff', 0.05),
                  },
                }}
              >
                Browse Library
              </Button>
            </Stack>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            <Box
              sx={{
                mt: 6,
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 3, md: 6 },
                flexWrap: 'wrap',
              }}
            >
              {[
                { value: '10K+', label: 'Titles' },
                { value: '50K+', label: 'Episodes' },
                { value: '1M+', label: 'Users' },
                { value: '4.9', label: 'Rating' },
              ].map((stat) => (
                <Box key={stat.label} sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #8B5CF6, #00B4D8)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#64748B', fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Box>
      </Container>

      {/* Bottom fade */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 200,
          background: 'linear-gradient(to top, #050505 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}
